import { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Card, Col, Form, Row } from 'react-bootstrap';
import BackButton from '../Components/BackButton';
import Loader from '../Components/Loader';
import Message from '../Components/Message';
import Meta from '../Components/Meta';
import Product from '../Components/Product';
import { useGetProductsQuery } from '../slices/productsApiSlice';
import {
  getEngineOilSearchText,
  getManufacturerOptions,
  matchesManufacturer,
} from '../utils/engineOilFilters';

const GRADE_ORDER = ['0W-16', '0W-20', '0W-30', '0W-40', '5W-20', '5W-30', '5W-40'];

export default function EngineOilScreen() {
  const [grade, setGrade] = useState('All');
  const [search, setSearch] = useState('');
  const [manufacturer, setManufacturer] = useState('');
  const { data, isLoading, error } = useGetProductsQuery({ category: 'engine-oil', all: true });
  const products = data?.products || [];
  const grades = useMemo(() => {
    const available = new Set(products.map((product) => product.attributes?.Viscosity).filter(Boolean));
    return ['All', ...GRADE_ORDER.filter((item) => available.has(item))];
  }, [products]);
  const gradeProducts = useMemo(() => products.filter((product) => {
    const viscosity = String(product.attributes?.Viscosity || product.partNumber || '').toUpperCase();
    return grade === 'All' || viscosity === grade;
  }), [products, grade]);
  const manufacturers = useMemo(() => getManufacturerOptions(gradeProducts), [gradeProducts]);

  useEffect(() => {
    if (manufacturer && !manufacturers.includes(manufacturer)) {
      setManufacturer('');
    }
  }, [manufacturer, manufacturers]);

  const filteredProducts = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return products
      .filter((product) => !needle || getEngineOilSearchText(product).toLowerCase().includes(needle))
      .filter((product) => {
        const viscosity = String(product.attributes?.Viscosity || product.partNumber || '').toUpperCase();
        return grade === 'All' || viscosity === grade;
      })
      .filter((product) => matchesManufacturer(product, manufacturer));
  }, [products, grade, manufacturer, search]);

  const clearFilters = () => {
    setGrade('All');
    setManufacturer('');
    setSearch('');
  };

  return (
    <div className="engine-oil-catalog py-3">
      <Meta
        title="Engine Oil | NORD KAPP Motor Oil | KCC Parts Canada"
        description="Shop NORD KAPP engine oil in 0W-16, 0W-20, 0W-30, 0W-40, 5W-20, 5W-30 and 5W-40 grades from KCC Parts Distribution in Canada."
        canonicalPath="/engine-oil"
      />
      <BackButton />
      <img
        src="/images/nord-kapp-engine-oil-banner.png"
        alt="NORD KAPP Engine Oil Product Data"
        className="engine-oil-page-banner"
      />

      <Alert variant="info" className="engine-oil-guide">
        <Alert.Heading as="h2" className="h5">How to choose the correct engine oil</Alert.Heading>
        <p>Your vehicle may require a specific oil approval in addition to the viscosity grade. Two oils can both be 0W-20 but meet different manufacturer specifications.</p>
        <p className="mb-2">Check your owner's manual, oil cap, or manufacturer service information for:</p>
        <ol className="mb-2">
          <li>the required viscosity, such as 0W-20</li>
          <li>the required specification or approval, such as GM dexos1, VW 508.00/509.00, BMW Longlife, MB 229.51, Ford WSS, or Porsche C30</li>
        </ol>
        <p className="mb-2">Then select the NORD KAPP oil that matches both requirements.</p>
        <strong>Do not choose engine oil by viscosity alone.</strong>
      </Alert>

      <Card className="border-0 shadow-sm mb-4">
        <Card.Body>
          <div className="d-flex justify-content-between align-items-center gap-3 mb-3">
            <h2 className="h5 mb-0">Find oil by specification</h2>
            <Button type="button" variant="link" className="p-0" onClick={clearFilters}>Clear filters</Button>
          </div>
          <Row className="g-3 align-items-end">
            <Col md={6} lg={4}>
              <Form.Label htmlFor="oil-search">Search products and specifications</Form.Label>
              <Form.Control id="oil-search" type="search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Try 0W-20, dexos, BMW, VW 504 or 229.51" />
            </Col>
            <Col md={6} lg={4}>
              <Form.Label htmlFor="oil-grade">Oil grade</Form.Label>
              <Form.Select id="oil-grade" value={grade} onChange={(e) => setGrade(e.target.value)}>
                {grades.map((item) => <option key={item}>{item}</option>)}
              </Form.Select>
            </Col>
            <Col md={12} lg={4}>
              <Form.Label htmlFor="oil-manufacturer">Manufacturer / OEM specification</Form.Label>
              <Form.Select id="oil-manufacturer" value={manufacturer} onChange={(e) => setManufacturer(e.target.value)}>
                <option value="">All manufacturers</option>
                {manufacturers.map((item) => <option key={item} value={item}>{item}</option>)}
              </Form.Select>
            </Col>
          </Row>
          <Row>
            <Col lg={{ span: 4, offset: 8 }}>
              <Form.Text muted>Available options depend on the selected oil grade.</Form.Text>
            </Col>
          </Row>
          <div className="d-flex flex-column flex-md-row justify-content-between gap-2 mt-3 text-muted small">
            <span>This filters oils by documented manufacturer specifications and does not confirm vehicle fitment.</span>
            <span>{filteredProducts.length} product{filteredProducts.length === 1 ? '' : 's'}</span>
          </div>
        </Card.Body>
      </Card>

      {isLoading && <Loader />}
      {error && <Message variant="danger">{error?.data?.message || error.error}</Message>}
      {!isLoading && !error && filteredProducts.length === 0 && (
        <Message>{products.length === 0 ? 'No Engine Oil products are available yet.' : 'No NORD KAPP engine oil matches the selected filters.'}</Message>
      )}

      <Row>
        {filteredProducts.map((product) => (
          <Col key={product._id} sm={12} md={6} lg={4} xl={3}>
            <Product product={product} />
          </Col>
        ))}
      </Row>
    </div>
  );
}
